import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import {
  StatusChip,
  billChipMeta,
  paymentStatusMeta,
} from "../../components/ui";
import { bucketFor } from "../../utils/billBucket";
import { EMPTY_VALUE, formatDate, formatMoney } from "../../utils/format";
import { monoFontFamily } from "../../theme/tokens";

export interface BillDataItem {
  programName?: string | null;
  amount?: number | null;
  taxable?: boolean | null;
  status?: string | null;
  requestingApplication?: string | null;
  requestingClient?: string | null;
  invoiceDate?: string | null;
  notes?: string | null;
  name?: string | null;
  // AB#5377: paymentService is optional on a bill; the ops repair tooling has created
  // bills without one.
  paymentService?: {
    billRefCode?: string | null;
    paymentStatus?: string | null;
    paymentRefCode?: string | null;
    paymentDate?: string | null;
  } | null;
}

interface PayeeBillsProps {
  billData: BillDataItem[];
  /** Shown at the right of the tab row, e.g. the "Showing: Paid" filter chip. */
  toolbar?: React.ReactNode;
  /** Message when there are no rows. */
  emptyMessage?: string;
}

const COLUMN_COUNT = 12;

function TabLabel({
  label,
  count,
  selected,
}: {
  label: string;
  count: number;
  selected: boolean;
}) {
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}
    >
      {label}
      <Chip
        size="small"
        label={count}
        color={selected ? "primary" : "default"}
        aria-label={`${count} ${count === 1 ? "bill" : "bills"}`}
        sx={{
          height: 20,
          fontSize: "0.6875rem",
          "& .MuiChip-label": { px: 1.5 },
        }}
      />
    </Box>
  );
}

export default function PayeeBills({
  billData,
  toolbar,
  emptyMessage = "No bills for this payee yet.",
}: PayeeBillsProps) {
  const [value, setValue] = React.useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Tabs value={value} onChange={handleChange} aria-label="Payee records">
          <Tab
            id="payee-tab-0"
            aria-controls="payee-tabpanel-0"
            label={
              <TabLabel
                label="Bills"
                count={billData.length}
                selected={value === 0}
              />
            }
          />
        </Tabs>
        {toolbar}
      </Box>

      <div
        role="tabpanel"
        hidden={value !== 0}
        id="payee-tabpanel-0"
        aria-labelledby="payee-tab-0"
      >
        {value === 0 && (
          <Card>
            <TableContainer>
              <Table size="small" aria-label="Bills">
                <TableHead>
                  <TableRow>
                    <TableCell>Application</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Bill RefCode</TableCell>
                    <TableCell>Bill Status</TableCell>
                    <TableCell align="right">Bill Amount</TableCell>
                    <TableCell>Taxable</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billData.map((row, index) => {
                    const ref = row.paymentService?.billRefCode ?? "";
                    const paymentStatus = row.paymentService?.paymentStatus;
                    // The shared bill-bucket rule (Bill detail, Dashboard): only a paid bill
                    // shows its payment date; a stale date on an unpaid bill reads "—".
                    const paid =
                      bucketFor(row.status, paymentStatus) === "paid";
                    return (
                      // By position too: AB#5377 bills can share a ref code.
                      <TableRow key={`${ref}-${index}`} hover>
                        <TableCell component="th" scope="row">
                          {row.requestingApplication || EMPTY_VALUE}
                        </TableCell>
                        <TableCell>
                          {row.requestingClient || EMPTY_VALUE}
                        </TableCell>
                        <TableCell>{row.programName || EMPTY_VALUE}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(row.invoiceDate)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: monoFontFamily,
                            fontSize: "0.75rem",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ref || EMPTY_VALUE}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            meta={billChipMeta(row.status, paymentStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatMoney(row.amount)}
                        </TableCell>
                        <TableCell>{row.taxable ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <StatusChip
                            meta={paymentStatusMeta(paymentStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(
                            paid ? row.paymentService?.paymentDate : null
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {row.notes ? (
                            <Tooltip title={row.notes} placement="top-start">
                              <Box
                                component="span"
                                sx={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.notes}
                              </Box>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </TableCell>
                        <TableCell align="center" padding="none">
                          {ref ? (
                            <IconButton
                              component={RouterLink}
                              to={`/bills/detail/${ref}`}
                              size="small"
                              aria-label={`View bill ${ref}`}
                              sx={{ color: "text.secondary" }}
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              disabled
                              aria-label="This bill has no reference to view"
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {billData.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        align="center"
                        sx={{ py: 8 }}
                      >
                        <Typography color="text.secondary">
                          {emptyMessage}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </div>
    </Box>
  );
}
